import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './services/firebase';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import PlansView from './pages/PlansView';
import AccountHome from './pages/AccountHome';
import PromptDetail from './pages/PromptDetail';
import ConversationView from './pages/ConversationView';
import ChannelsView from './pages/ChannelsView';
import MembersView from './pages/MembersView';

function SysAdminAccountHome({ logout }) {
  const { accountId } = useParams();
  const navigate = useNavigate();
  return (
    <AccountHome
      accountId={accountId}
      role="admin"
      isSysAdmin
      onOpen={(v) => navigate(`/accounts/${accountId}/${v}`)}
      onBack={() => navigate('/')}
      onLogout={logout}
    />
  );
}

function SysAdminSubpage({ component: Component, logout, extraProps = {} }) {
  const { accountId } = useParams();
  const navigate = useNavigate();
  return (
    <Component
      accountId={accountId}
      onBack={() => navigate(`/accounts/${accountId}`)}
      onLogout={logout}
      {...extraProps}
    />
  );
}

function AppContent({ authReady, user, profile, loadProfile, logout }) {
  const navigate = useNavigate();

  if (!authReady) return <div className="app-loading">Carregando…</div>;

  if (!user) {
    return (
      <Routes>
        <Route path="/signup" element={
          <Signup
            onDone={() => loadProfile(auth.currentUser.uid)}
            onGoLogin={() => navigate('/login')}
          />
        } />
        <Route path="*" element={
          <Login onGoSignup={() => navigate('/signup')} />
        } />
      </Routes>
    );
  }

  if (!profile) {
    return <Onboarding onDone={() => loadProfile(auth.currentUser.uid)} />;
  }

  const isSysAdmin = profile.platformRole === 'system_admin';

  if (isSysAdmin) {
    return (
      <Routes>
        <Route path="/" element={
          <Dashboard
            onSelect={(id) => navigate(`/accounts/${id}`)}
            onOpenPlans={() => navigate('/plans')}
            onLogout={logout}
          />
        } />
        <Route path="/plans" element={
          <PlansView onBack={() => navigate('/')} onLogout={logout} />
        } />
        <Route path="/accounts/:accountId" element={<SysAdminAccountHome logout={logout} />} />
        <Route path="/accounts/:accountId/conversations" element={
          <SysAdminSubpage component={ConversationView} logout={logout} />
        } />
        <Route path="/accounts/:accountId/prompt" element={
          <SysAdminSubpage component={PromptDetail} logout={logout} extraProps={{ role: 'admin' }} />
        } />
        <Route path="/accounts/:accountId/channels" element={
          <SysAdminSubpage component={ChannelsView} logout={logout} />
        } />
        <Route path="/accounts/:accountId/members" element={
          <SysAdminSubpage component={MembersView} logout={logout} />
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const accountId = profile.accountId;
  const role = profile.role;

  return (
    <Routes>
      <Route path="/" element={
        <AccountHome
          accountId={accountId}
          role={role}
          isSysAdmin={false}
          onOpen={(v) => navigate(`/${v}`)}
          onLogout={logout}
        />
      } />
      <Route path="/conversations" element={
        <ConversationView accountId={accountId} onBack={() => navigate('/')} onLogout={logout} />
      } />
      <Route path="/prompt" element={
        <PromptDetail accountId={accountId} role={role} onBack={() => navigate('/')} onLogout={logout} />
      } />
      <Route path="/channels" element={
        <ChannelsView accountId={accountId} onBack={() => navigate('/')} onLogout={logout} />
      } />
      <Route path="/members" element={
        <MembersView accountId={accountId} onBack={() => navigate('/')} onLogout={logout} />
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const { t } = useTranslation();
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const loadProfile = async (uid) => {
    const snap = await getDoc(doc(db, 'users', uid));
    setProfile(snap.exists() ? snap.data() : null);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthReady(false);
      setUser(u);
      if (u) await loadProfile(u.uid);
      else setProfile(null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  const logout = async () => {
    await signOut(auth);
    setProfile(null);
    setUser(null);
  };

  return (
    <BrowserRouter>
      <AppContent
        authReady={authReady}
        user={user}
        profile={profile}
        loadProfile={loadProfile}
        logout={logout}
      />
    </BrowserRouter>
  );
}
